import { FC, useRef } from 'react';

import { Typography } from '@material-ui/core';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import { GridColDef } from '@material-ui/data-grid';
import AddIcon from '@material-ui/icons/Add';
import FilterDrawer from 'src/components/FilterDrawer';
import { FilterDrawerRef } from 'src/components/FilterDrawer/interfaces';
import LinkButton from 'src/components/LinkButton';
import Table from 'src/components/Table';
import TableActions from 'src/components/Table/Actions';
import StatusIcon from 'src/components/Table/StatusIcon';
import usePagination from 'src/hooks/usePagination';
import IBeneficiary from 'src/interfaces/models/IBeneficiary';
import masks from 'src/utils/masks';
import BeneficiaryFilterSchema from 'src/validators/Beneficiary/filter.schema';

import Filters from './Filters';
import { Container } from './styles';

const List: FC = () => {
  const filterRef = useRef<FilterDrawerRef>(null);
  const {
    handlePageChange,
    handlePageSizeChange,
    data,
    loading,
    info,
    loadData,
  } = usePagination<IBeneficiary>('admin/beneficiaries');

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID' },
    {
      field: 'document',
      headerName: 'CNPJ',
      width: 170,
      valueGetter({ row }) {
        return masks.cnpj(row.headquarter.document);
      },
    },
    { field: 'display_name', headerName: 'Nome', flex: 1 },
    {
      field: 'city',
      headerName: 'Cidade',
      flex: 0.5,
      valueGetter({ row }) {
        return `${row.headquarter.city} - ${row.headquarter.state}`;
      },
    },
    {
      field: 'qty_employess',
      headerName: 'Qtde Beneficiários',
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'status',
      headerName: 'Status',
      headerAlign: 'center',
      align: 'center',
      renderCell(params) {
        return <StatusIcon status={params.row.status} />;
      },
    },
    {
      field: '',
      disableColumnMenu: true,
      sortable: false,
      align: 'center',
      renderCell({ id, row }) {
        return (
          <TableActions
            resource="Beneficiária"
            resourceStatus={row.status}
            editLink={`/beneficiarias/${id}`}
            deleteApiUrl={`/admin/beneficiaries/${id}`}
            restoreApiUrl={`/admin/beneficiaries/${id}/restore`}
            descriptionPrefix={`A beneficiária ${row.display_name}`}
            onFinish={loadData}
          />
        );
      },
    },
  ];

  return (
    <Container>
      <FilterDrawer
        ref={filterRef}
        columns={columns.filter((column) => !column.hide && column.field)}
        yupSchema={BeneficiaryFilterSchema}
      >
        <Filters />
      </FilterDrawer>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Grid container justify="space-between" alignItems="center">
            <Typography variant="h5">Beneficiárias</Typography>

            <LinkButton
              to="/beneficiarias/novo"
              startIcon={<AddIcon />}
              color="primary"
              variant="contained"
              size="small"
            >
              Novo
            </LinkButton>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Table
                autoHeight
                rows={data}
                page={info.current_page - 1}
                pageSize={info.per_page}
                rowCount={info.total}
                columns={columns}
                loading={loading}
                pagination
                paginationMode="server"
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                onFilterClick={filterRef.current?.open}
                canSearch
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default List;
